import { defineStore } from 'pinia'
import { authApi } from '@/api'
import { registerAuthHooks } from '@/api/client'
import type { AdminOut } from '@/api/types'

interface AuthState {
  token: string | null
  refreshToken: string | null
  admin: AdminOut | null
  loading: boolean
}

const TOKEN_KEY = 'esim_admin_token'
const REFRESH_KEY = 'esim_admin_refresh'

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: localStorage.getItem(TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_KEY),
    admin: null,
    loading: false,
  }),

  actions: {
    persist() {
      if (this.token) localStorage.setItem(TOKEN_KEY, this.token)
      else localStorage.removeItem(TOKEN_KEY)
      if (this.refreshToken) localStorage.setItem(REFRESH_KEY, this.refreshToken)
      else localStorage.removeItem(REFRESH_KEY)
    },

    async loginPassword(username: string, password: string) {
      this.loading = true
      try {
        const tokens = await authApi.loginPassword(username, password)
        this.token = tokens.access_token
        this.refreshToken = tokens.refresh_token
        this.persist()
        await this.fetchMe()
      } finally {
        this.loading = false
      }
    },

    async loginTelegram(initData: string) {
      const tokens = await authApi.loginTelegram(initData)
      this.token = tokens.access_token
      this.refreshToken = tokens.refresh_token
      this.persist()
      await this.fetchMe()
    },

    async fetchMe() {
      try {
        this.admin = await authApi.me()
      } catch {
        this.admin = null
      }
    },

    async refresh(): Promise<boolean> {
      if (!this.refreshToken) return false
      try {
        const t = await authApi.refresh(this.refreshToken)
        this.token = t.access_token
        this.refreshToken = t.refresh_token
        this.persist()
        return true
      } catch {
        this.logout()
        return false
      }
    },

    /** Попробовать «бесшумно» восстановить сессию — либо из localStorage,
     *  либо через Telegram WebApp initData (если открыто из Telegram). */
    async tryRestore() {
      if (this.token) {
        await this.fetchMe()
        if (this.admin) return
        // токен невалиден — пробуем refresh
        if (await this.refresh()) {
          await this.fetchMe()
          if (this.admin) return
        }
      }
      const initData = window.Telegram?.WebApp?.initData
      if (initData) {
        try {
          await this.loginTelegram(initData)
        } catch {
          /* пользователь не в admin_user — попадёт на /login */
        }
      }
    },

    logout() {
      this.token = null
      this.refreshToken = null
      this.admin = null
      this.persist()
    },
  },
})

// «Подвязываем» axios-инстанс к store. Делаем это лениво в первом обращении.
let hooked = false
export function ensureAuthHooks() {
  if (hooked) return
  hooked = true
  const store = useAuthStore()
  registerAuthHooks({
    getAccessToken: () => store.token,
    refresh: () => store.refresh(),
    onFail: () => {
      store.logout()
      // редирект — в guard router'а.
    },
  })
}
